import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Upload, X, CheckCircle, FileText, Loader2, 
  Building2, Shield, TrendingUp, Check, AlertCircle,
  ArrowRight, ArrowLeft, DollarSign, FileCheck
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import SEO from '../components/shared/SEO';

// Pre-qualification Question Component
const PreQualQuestion = ({ question, value, onChange }) => (
  <div className="mb-5">
    <p className="text-white text-base mb-3">{question}</p>
    <div className="flex gap-3">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`flex-1 py-3 px-4 border transition-all flex items-center justify-center gap-2 text-sm ${
          value === true 
            ? 'bg-emerald-500/10 border-emerald-500 text-emerald-400' 
            : 'bg-zinc-900 border-zinc-700 text-white hover:border-zinc-500'
        }`}
      >
        <Check className="w-4 h-4" />
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`flex-1 py-3 px-4 border transition-all flex items-center justify-center gap-2 text-sm ${
          value === false 
            ? 'bg-red-500/10 border-red-500 text-red-400' 
            : 'bg-zinc-900 border-zinc-700 text-white hover:border-zinc-500'
        }`}
      >
        <X className="w-4 h-4" />
        No
      </button>
    </div>
  </div>
);

// Pre-qualification Agreement Checkbox
const PreQualCheckbox = ({ text, checked, onChange }) => (
  <label className="flex items-start gap-3 mb-3 cursor-pointer group">
    <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center border transition-all mt-0.5 ${
      checked 
        ? 'bg-[#D4AF37] border-[#D4AF37]' 
        : 'bg-zinc-900 border-zinc-700 group-hover:border-zinc-500'
    }`}>
      {checked && <Check className="w-3 h-3 text-black" />}
    </div>
    <span className="text-white/70 text-sm leading-relaxed">{text}</span>
    <input 
      type="checkbox" 
      checked={checked} 
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
  </label>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service definitions with tiers
const SERVICES = {
  build: {
    id: 'build',
    name: 'Build',
    subtitle: 'New Authority Setup',
    description: 'Complete authority launch system with compliance foundation',
    icon: Building2,
    color: '#CD7F32', // Bronze
    tiers: [
      { id: 'foundation', name: 'Foundation', price: 997, support: '30/30 days', description: '30 Days Compliance + 30 Days Dispatch' },
      { id: 'accelerator', name: 'Accelerator', price: 1997, support: '60/60 days', description: '60 Days Compliance + 60 Days Dispatch' },
      { id: 'elite', name: 'Elite', price: 2997, support: '90/90 days', description: '90 Days Compliance + 90 Days Dispatch', popular: true }
    ]
  },
  protect: {
    id: 'protect',
    name: 'Protect',
    subtitle: 'Compliance Management',
    description: 'Ongoing compliance monitoring and deadline management',
    icon: Shield,
    color: '#C0C0C0', // Silver
    tiers: [
      { id: 'basic', name: 'Basic', price: 99, period: 'month', description: 'Essential compliance tracking' },
      { id: 'professional', name: 'Professional', price: 199, period: 'month', description: 'Full compliance management', popular: true },
      { id: 'enterprise', name: 'Enterprise', price: 399, period: 'month', description: 'Premium fleet compliance' }
    ]
  },
  grow: {
    id: 'grow',
    name: 'Grow',
    subtitle: 'Dispatch Services',
    description: 'Revenue-focused dispatch operations and load management',
    icon: TrendingUp,
    color: '#D4AF37', // Gold
    tiers: [
      { id: 'bronze', name: 'Bronze', price: 300, period: 'week', description: 'Dedicated dispatcher support' },
      { id: 'silver', name: 'Silver', price: 400, period: 'week', description: 'Extended hours + rate negotiation', popular: true },
      { id: 'gold', name: 'Gold', price: 500, period: 'week', description: 'Priority access + full optimization' }
    ]
  }
};

const CarrierOnboardingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    dot_number: '',
    mc_number: '',
    equipment_type: '',
    ready_to_start: '',
    news_signup: false,
    agree_terms: false,
  });
  
  // Service selections
  const [selectedServices, setSelectedServices] = useState({
    build: false,
    protect: false,
    grow: false
  });
  
  // Tier selections for each service
  const [selectedTiers, setSelectedTiers] = useState({
    build: '',
    protect: '',
    grow: ''
  });
  
  // Pre-qualification state for Build service
  const [showBuildPreQual, setShowBuildPreQual] = useState(false);
  const [buildPreQualStep, setBuildPreQualStep] = useState(1);
  const [buildQualified, setBuildQualified] = useState(false);
  const [preQualData, setPreQualData] = useState({
    // Step 1 - Business Status
    has_active_mc: null,
    llc_formed: null,
    ready_to_form_llc: null,
    // Step 2 - Financial Readiness
    understands_fees: null,
    has_startup_funds: null,
    can_secure_insurance: null,
    // Step 3 - Agreements
    understands_guidance: false,
    understands_no_filing: false,
    agrees_to_deadlines: false,
  });
  
  const [coiFile, setCoiFile] = useState(null);
  const [w9File, setW9File] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const coiInputRef = useRef(null);
  const w9InputRef = useRef(null);

  // Pre-qualification step validation
  const isPreQualStep1Complete = () => {
    if (preQualData.has_active_mc === true) return true; // Will redirect
    return preQualData.has_active_mc === false && 
           (preQualData.llc_formed === true || preQualData.ready_to_form_llc === true);
  };

  const isPreQualStep2Complete = () => {
    return preQualData.understands_fees === true && 
           preQualData.has_startup_funds === true && 
           preQualData.can_secure_insurance === true;
  };

  const isPreQualStep3Complete = () => {
    return preQualData.understands_guidance && 
           preQualData.understands_no_filing && 
           preQualData.agrees_to_deadlines;
  };

  const isFullyQualified = () => {
    return preQualData.has_active_mc === false &&
           (preQualData.llc_formed === true || preQualData.ready_to_form_llc === true) &&
           preQualData.understands_fees === true &&
           preQualData.has_startup_funds === true &&
           preQualData.can_secure_insurance === true &&
           preQualData.understands_guidance &&
           preQualData.understands_no_filing &&
           preQualData.agrees_to_deadlines;
  };

  const handleBuildServiceClick = () => {
    if (selectedServices.build) {
      // Deselecting Build
      setSelectedServices(prev => ({ ...prev, build: false }));
      setSelectedTiers(prev => ({ ...prev, build: '' }));
      setBuildQualified(false);
    } else {
      // Selecting Build - show pre-qualification
      setShowBuildPreQual(true);
      setBuildPreQualStep(1);
    }
  };

  const handlePreQualNext = () => {
    if (buildPreQualStep === 1 && preQualData.has_active_mc === true) {
      // Redirect to existing authority page
      navigate('/build/existing-authority');
      return;
    }
    
    if (buildPreQualStep < 3) {
      setBuildPreQualStep(buildPreQualStep + 1);
    } else {
      // Complete pre-qualification
      if (isFullyQualified()) {
        setBuildQualified(true);
        setSelectedServices(prev => ({ ...prev, build: true }));
        setShowBuildPreQual(false);
      }
    }
  };

  const handlePreQualBack = () => {
    if (buildPreQualStep > 1) {
      setBuildPreQualStep(buildPreQualStep - 1);
    } else {
      setShowBuildPreQual(false);
    }
  };

  const canProceedPreQual = () => {
    switch (buildPreQualStep) {
      case 1: return isPreQualStep1Complete();
      case 2: return isPreQualStep2Complete();
      case 3: return isPreQualStep3Complete();
      default: return false;
    }
  };

  const handleServiceToggle = (serviceId) => {
    if (serviceId === 'build') {
      handleBuildServiceClick();
      return;
    }
    
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
    // Clear tier if service is deselected
    if (selectedServices[serviceId]) {
      setSelectedTiers(prev => ({ ...prev, [serviceId]: '' }));
    }
  };

  const handleTierSelect = (serviceId, tierId) => {
    setSelectedTiers(prev => ({ ...prev, [serviceId]: tierId }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'coi') {
        setCoiFile(file);
      } else {
        setW9File(file);
      }
    }
  };

  const removeFile = (type) => {
    if (type === 'coi') {
      setCoiFile(null);
      if (coiInputRef.current) coiInputRef.current.value = '';
    } else {
      setW9File(null);
      if (w9InputRef.current) w9InputRef.current.value = '';
    }
  };

  // Calculate total
  const calculateTotal = () => {
    let total = 0;
    let items = [];
    
    Object.entries(selectedServices).forEach(([serviceId, isSelected]) => {
      if (isSelected && selectedTiers[serviceId]) {
        const service = SERVICES[serviceId];
        const tier = service.tiers.find(t => t.id === selectedTiers[serviceId]);
        if (tier) {
          items.push({ service: service.name, tier: tier.name, price: tier.price, period: tier.period });
          total += tier.price;
        }
      }
    });
    
    return { total, items };
  };

  const hasSelectedServices = Object.values(selectedServices).some(v => v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agree_terms) {
      setError('Please agree to the Service Approval Policy and Terms of Service.');
      return;
    }
    
    if (!hasSelectedServices) {
      setError('Please select at least one service.');
      return;
    }
    
    // Check that selected services have tiers
    const missingTiers = Object.entries(selectedServices)
      .filter(([id, selected]) => selected && !selectedTiers[id])
      .map(([id]) => SERVICES[id].name);
    
    if (missingTiers.length > 0) {
      setError(`Please select a tier for: ${missingTiers.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    const submitData = new FormData();
    // Add form fields
    submitData.append('company_name', formData.company_name);
    submitData.append('first_name', formData.first_name);
    submitData.append('last_name', formData.last_name);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('dot_number', formData.dot_number);
    submitData.append('mc_number', formData.mc_number || '');
    submitData.append('equipment_type', formData.equipment_type);
    submitData.append('ready_to_start', formData.ready_to_start);
    submitData.append('agree_terms', formData.agree_terms);
    submitData.append('news_signup', formData.news_signup);
    
    // Add service selections as JSON
    submitData.append('selected_services', JSON.stringify(selectedServices));
    submitData.append('selected_tiers', JSON.stringify(selectedTiers));
    
    if (coiFile) submitData.append('coi_file', coiFile);
    if (w9File) submitData.append('w9_file', w9File);

    try {
      const response = await axios.post(`${API}/application/submit`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Track referral if present in URL (non-blocking)
      try {
        const params = new URLSearchParams(window.location.search);
        const refCode = params.get('ref');
        if (refCode && formData.email) {
          axios.post(`${API}/referrals/track`, {
            ref_code: refCode,
            applicant_email: formData.email,
          }).catch(() => {});
        }
      } catch (e) { /* noop */ }

      // If checkout URL is provided, redirect to Stripe
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        // No payment required or payment setup failed - show success
        setIsSubmitted(true);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to submit application. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    const { items } = calculateTotal();
    
    return (
      <div data-testid="carrier-onboarding-success">
        <section className="page-header">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-black text-white uppercase">
              Application<br />
              <span className="text-[#D4AF37]">Submitted</span>
            </h1>
          </div>
        </section>
        
        <section className="py-16 md:py-24">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <CheckCircle className="w-24 h-24 text-[#D4AF37] mx-auto mb-8" />
            <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-4">
              Your Application Is Under Review
            </h2>
            <p className="text-white/60 text-lg mb-8">
              Thank you for applying to the Queen B Logistics ecosystem. Our team will review your 
              information within 24-48 hours and send you next steps via email.
            </p>
            
            {/* Services Applied For */}
            {items.length > 0 && (
              <div className="bg-[#121212] border border-white/10 p-6 mb-8 text-left">
                <h3 className="text-white font-bold mb-4">Services Applied For:</h3>
                <div className="space-y-2">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-white/70">
                      <span>{item.service} - {item.tier}</span>
                      <span className="text-[#D4AF37]">${item.price}{item.period ? `/${item.period}` : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="bg-[#121212] border border-white/10 p-8 text-left mb-8">
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#D4AF37] mb-4">
                What Happens Next?
              </h3>
              <ol className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] font-bold">1.</span>
                  <div>
                    <span className="text-white font-medium">Review</span>
                    <p className="text-sm">We review your authority status and service selection within 24–48 hours.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] font-bold">2.</span>
                  <div>
                    <span className="text-white font-medium">Approval + Welcome Email</span>
                    <p className="text-sm">You receive approval instructions, invoice (if applicable), and Hive access details.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] font-bold">3.</span>
                  <div>
                    <span className="text-white font-medium">Onboarding Activation</span>
                    <p className="text-sm">
                      • Build clients: Authority roadmap + timers begin<br />
                      • Protect clients: Compliance calendar activated<br />
                      • Grow clients: Dispatch setup call scheduled
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] font-bold">4.</span>
                  <div>
                    <span className="text-white font-medium">Operations Begin</span>
                    <p className="text-sm">Your system goes live under the Build • Protect • Grow™ framework.</p>
                  </div>
                </li>
              </ol>
            </div>
            
            <Link to="/" className="btn-primary inline-block">
              Return Home
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const { total, items } = calculateTotal();

  return (
    <div data-testid="carrier-onboarding-page">
      <SEO 
        title="Apply Now - Build • Protect • Grow™ | Queen B Logistics"
        description="Apply for Queen B Logistics services. Authority setup (Build), compliance management (Protect), or dispatch services (Grow). Complete carrier onboarding application."
        canonical="https://queenblogistics.com/carrier-onboarding"
        keywords="carrier application, authority setup, trucking compliance, dispatch services, owner operator application, freight dispatch application"
      />
      
      {/* Page Header */}
      <section className="page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="limited-badge mb-6">
            Build • Protect • Grow™
          </div>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase">
            Queen B Logistics<br />
            <span className="text-[#D4AF37]">Application</span>
          </h1>
          <p className="text-white/70 mt-4 text-lg max-w-2xl">
            Your entry point into the Queen B Logistics ecosystem. Apply for authority setup, 
            compliance management, or dispatch services — or all three.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              {/* Framework Introduction */}
              <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border-l-4 border-[#D4AF37] p-6 mb-8">
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-3">
                  Build. Protect. Grow.™
                </h3>
                <p className="text-white/70 mb-4">
                  Queen B Logistics provides structured authority setup, compliance protection, 
                  and revenue-focused dispatch — designed for owner-operators and small fleets 
                  who want to launch, stay compliant, and grow profitably.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-[#CD7F32]">
                    <Building2 className="w-4 h-4" />
                    <span>Build: Authority Setup</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#C0C0C0]">
                    <Shield className="w-4 h-4" />
                    <span>Protect: Compliance</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <TrendingUp className="w-4 h-4" />
                    <span>Grow: Dispatch</span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8" data-testid="onboarding-form">
                {/* SERVICE SELECTION SECTION */}
                <div className="bg-[#121212] border border-white/10 p-6">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-2">
                    Which Services Are You Applying For?
                  </h3>
                  <p className="text-white/50 text-sm mb-6">Multi-select allowed</p>
                  
                  <div className="space-y-6">
                    {Object.values(SERVICES).map((service) => {
                      const Icon = service.icon;
                      const isSelected = selectedServices[service.id];
                      const isBuild = service.id === 'build';
                      const showQualifiedBadge = isBuild && buildQualified && isSelected;
                      
                      return (
                        <div key={service.id} className="space-y-4">
                          {/* Service Checkbox */}
                          <div 
                            className={`flex items-start gap-4 p-4 border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                                : 'border-white/10 hover:border-white/20'
                            }`}
                            onClick={() => handleServiceToggle(service.id)}
                            data-testid={`service-${service.id}`}
                          >
                            <div 
                              className="w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ borderColor: service.color }}
                            >
                              {isSelected && <Check className="w-4 h-4" style={{ color: service.color }} />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Icon className="w-5 h-5" style={{ color: service.color }} />
                                <span className="text-white font-bold">{service.name}</span>
                                <span className="text-white/50 text-sm">({service.subtitle})</span>
                                {showQualifiedBadge && (
                                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-bold">
                                    QUALIFIED
                                  </span>
                                )}
                                {isBuild && !isSelected && (
                                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs">
                                    Pre-qualification required
                                  </span>
                                )}
                              </div>
                              <p className="text-white/60 text-sm">{service.description}</p>
                            </div>
                          </div>
                          
                          {/* Tier Selection (shown when service is selected) */}
                          {isSelected && (
                            <div className="ml-10 grid grid-cols-1 md:grid-cols-3 gap-3">
                              {service.tiers.map((tier) => (
                                <div
                                  key={tier.id}
                                  className={`relative p-4 border cursor-pointer transition-all ${
                                    selectedTiers[service.id] === tier.id
                                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                                      : 'border-white/10 hover:border-white/20'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTierSelect(service.id, tier.id);
                                  }}
                                  data-testid={`tier-${service.id}-${tier.id}`}
                                >
                                  {tier.popular && (
                                    <div className="absolute -top-2 right-2 px-2 py-0.5 bg-[#D4AF37] text-black text-xs font-bold">
                                      POPULAR
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 mb-2">
                                    <div 
                                      className={`w-4 h-4 rounded-full border-2 ${
                                        selectedTiers[service.id] === tier.id 
                                          ? 'bg-[#D4AF37] border-[#D4AF37]' 
                                          : 'border-white/30'
                                      }`}
                                    />
                                    <span className="text-white font-medium">{tier.name}</span>
                                  </div>
                                  <p className="text-[#D4AF37] font-bold">
                                    ${tier.price}
                                    {tier.period && <span className="text-white/50 font-normal">/{tier.period}</span>}
                                    {tier.support && <span className="text-white/50 font-normal text-xs block">{tier.support}</span>}
                                  </p>
                                  <p className="text-white/50 text-xs mt-1">{tier.description}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* OPERATIONAL FIELDS */}
                <div className="space-y-6">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white">
                    Company Information
                  </h3>
                  
                  {/* Company Name */}
                  <div>
                    <Label htmlFor="company_name" className="text-white/70 mb-2 block">
                      Company Name <span className="text-[#D4AF37]">*</span>
                    </Label>
                    <Input
                      id="company_name"
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Your company name"
                      data-testid="onboarding-company-input"
                    />
                  </div>

                  {/* Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="first_name" className="text-white/70 mb-2 block">
                        First Name <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="first_name"
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="input-dark w-full"
                        placeholder="First name"
                        data-testid="onboarding-firstname-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name" className="text-white/70 mb-2 block">
                        Last Name <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="last_name"
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="input-dark w-full"
                        placeholder="Last name"
                        data-testid="onboarding-lastname-input"
                      />
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-white/70 mb-2 block">
                        Email <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-dark w-full"
                        placeholder="your@email.com"
                        data-testid="onboarding-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-white/70 mb-2 block">
                        Phone <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-dark w-full"
                        placeholder="(555) 555-5555"
                        data-testid="onboarding-phone-input"
                      />
                    </div>
                  </div>

                  {/* DOT & MC Number - Stored Separately */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="dot_number" className="text-white/70 mb-2 block">
                        DOT Number <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="dot_number"
                        type="text"
                        required
                        value={formData.dot_number}
                        onChange={(e) => setFormData({ ...formData, dot_number: e.target.value })}
                        className="input-dark w-full"
                        placeholder="USDOT# (or 'Applying' if new)"
                        data-testid="onboarding-dot-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mc_number" className="text-white/70 mb-2 block">
                        MC Number <span className="text-white/40">(if applicable)</span>
                      </Label>
                      <Input
                        id="mc_number"
                        type="text"
                        value={formData.mc_number}
                        onChange={(e) => setFormData({ ...formData, mc_number: e.target.value })}
                        className="input-dark w-full"
                        placeholder="MC# (leave blank if new authority)"
                        data-testid="onboarding-mc-input"
                      />
                    </div>
                  </div>

                  {/* Equipment Type & Ready to Start */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white/70 mb-2 block">
                        Equipment Type <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Select
                        value={formData.equipment_type}
                        onValueChange={(value) => setFormData({ ...formData, equipment_type: value })}
                        required
                      >
                        <SelectTrigger className="input-dark w-full" data-testid="onboarding-equipment-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dry-van">Dry Van</SelectItem>
                          <SelectItem value="reefer">Reefer</SelectItem>
                          <SelectItem value="flatbed">Flatbed</SelectItem>
                          <SelectItem value="step-deck">Step Deck</SelectItem>
                          <SelectItem value="box-truck">Box Truck</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/70 mb-2 block">
                        How soon are you ready to start? <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Select
                        value={formData.ready_to_start}
                        onValueChange={(value) => setFormData({ ...formData, ready_to_start: value })}
                        required
                      >
                        <SelectTrigger className="input-dark w-full" data-testid="onboarding-ready-select">
                          <SelectValue placeholder="Select timeframe" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediately">Immediately</SelectItem>
                          <SelectItem value="1-2-weeks">1-2 Weeks</SelectItem>
                          <SelectItem value="1-month">Within a Month</SelectItem>
                          <SelectItem value="just-exploring">Just Exploring</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* File Uploads */}
                  <div>
                    <Label className="text-white/70 mb-4 block">
                      Upload Documents (Optional - can be provided later)
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* COI Upload */}
                      <div>
                        <input
                          type="file"
                          ref={coiInputRef}
                          onChange={(e) => handleFileChange(e, 'coi')}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          data-testid="onboarding-coi-input"
                        />
                        {coiFile ? (
                          <div className="p-4 bg-[#121212] border border-[#D4AF37]/30 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-[#D4AF37]" />
                              <span className="text-white text-sm truncate max-w-[150px]">{coiFile.name}</span>
                            </div>
                            <button 
                              type="button" 
                              onClick={() => removeFile('coi')}
                              className="text-white/50 hover:text-red-500"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => coiInputRef.current?.click()}
                            className="upload-zone w-full"
                          >
                            <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                            <span className="text-white/50 text-sm">Upload COI</span>
                          </button>
                        )}
                      </div>

                      {/* W9 Upload */}
                      <div>
                        <input
                          type="file"
                          ref={w9InputRef}
                          onChange={(e) => handleFileChange(e, 'w9')}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          data-testid="onboarding-w9-input"
                        />
                        {w9File ? (
                          <div className="p-4 bg-[#121212] border border-[#D4AF37]/30 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-[#D4AF37]" />
                              <span className="text-white text-sm truncate max-w-[150px]">{w9File.name}</span>
                            </div>
                            <button 
                              type="button" 
                              onClick={() => removeFile('w9')}
                              className="text-white/50 hover:text-red-500"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => w9InputRef.current?.click()}
                            className="upload-zone w-full"
                          >
                            <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                            <span className="text-white/50 text-sm">Upload W9</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="news_signup"
                        checked={formData.news_signup}
                        onCheckedChange={(checked) => setFormData({ ...formData, news_signup: checked })}
                        className="mt-1"
                        data-testid="onboarding-news-checkbox"
                      />
                      <Label htmlFor="news_signup" className="text-white/70 text-sm cursor-pointer">
                        Sign up for news and updates
                      </Label>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="agree_terms"
                        checked={formData.agree_terms}
                        onCheckedChange={(checked) => setFormData({ ...formData, agree_terms: checked })}
                        className="mt-1"
                        data-testid="onboarding-terms-checkbox"
                      />
                      <Label htmlFor="agree_terms" className="text-white/70 text-sm cursor-pointer">
                        I agree to the{' '}
                        <Link to="/service-approval-policy" className="text-[#D4AF37] hover:underline">
                          Service Approval Policy
                        </Link>{' '}
                        and{' '}
                        <Link to="/terms-of-service" className="text-[#D4AF37] hover:underline">
                          Terms of Service
                        </Link>
                      </Label>
                    </div>
                  </div>
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !hasSelectedServices}
                  className="btn-primary w-full flex items-center justify-center gap-2 gold-glow disabled:opacity-50"
                  data-testid="onboarding-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting Application...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </button>
                
                <p className="text-white/40 text-xs text-center">
                  This is an application only. Hive access is granted after admin approval and payment (if applicable).
                </p>
              </form>
            </div>

            {/* Sidebar */}
            <div>
              <div className="sticky top-24 space-y-6">
                {/* Selected Services Summary */}
                {hasSelectedServices && (
                  <div className="bg-[#121212] border border-[#D4AF37]/30 p-6">
                    <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#D4AF37] mb-4">
                      Your Selection
                    </h3>
                    <div className="space-y-3 mb-4">
                      {items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-white">{item.service} - {item.tier}</span>
                          <span className="text-[#D4AF37]">${item.price}{item.period ? `/${item.period}` : ''}</span>
                        </div>
                      ))}
                    </div>
                    {items.length > 1 && (
                      <div className="border-t border-white/10 pt-3">
                        <div className="flex justify-between font-bold">
                          <span className="text-white">Total</span>
                          <span className="text-[#D4AF37]">${total}</span>
                        </div>
                        <p className="text-white/40 text-xs mt-1">
                          *Pricing varies by billing period
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* What Happens Next */}
                <div className="bg-[#121212] border border-white/5 p-6">
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#D4AF37] mb-6">
                    What Happens Next?
                  </h3>
                  <ol className="space-y-5">
                    {[
                      { 
                        step: '1', 
                        title: 'Review', 
                        desc: 'We review your authority status and service selection within 24–48 hours.' 
                      },
                      { 
                        step: '2', 
                        title: 'Approval + Welcome Email', 
                        desc: 'You receive approval instructions, invoice (if applicable), and Hive access details.' 
                      },
                      { 
                        step: '3', 
                        title: 'Onboarding Activation', 
                        desc: 'Build: roadmap + timers • Protect: calendar activated • Grow: dispatch call' 
                      },
                      { 
                        step: '4', 
                        title: 'Operations Begin', 
                        desc: 'Your system goes live under Build • Protect • Grow™' 
                      },
                    ].map((item) => (
                      <li key={item.step} className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                          <span className="text-black font-bold text-sm">{item.step}</span>
                        </div>
                        <div>
                          <h4 className="text-white font-bold text-sm">{item.title}</h4>
                          <p className="text-white/50 text-xs">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Build Pre-Qualification Modal */}
      {showBuildPreQual && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="build-prequal-modal">
          <div className="bg-[#121212] border border-zinc-800 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#121212] border-b border-zinc-800 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#CD7F32]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#CD7F32]" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Build Pre-Qualification</h3>
                  <p className="text-zinc-500 text-xs">Step {buildPreQualStep} of 3</p>
                </div>
              </div>
              <button 
                onClick={() => setShowBuildPreQual(false)}
                className="text-zinc-500 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Step Progress */}
            <div className="flex items-center justify-center gap-2 p-4 bg-zinc-900/50">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold transition-all ${
                    step < buildPreQualStep 
                      ? 'bg-emerald-500 text-white' 
                      : step === buildPreQualStep 
                        ? 'bg-[#D4AF37] text-black' 
                        : 'bg-zinc-800 text-zinc-500'
                  }`}>
                    {step < buildPreQualStep ? <Check className="w-4 h-4" /> : step}
                  </div>
                  {step < 3 && (
                    <div className={`w-8 h-0.5 ${step < buildPreQualStep ? 'bg-emerald-500' : 'bg-zinc-800'}`} />
                  )}
                </div>
              ))}
            </div>
            
            {/* Step Content */}
            <div className="p-6">
              {/* Step 1 - Business Status */}
              {buildPreQualStep === 1 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-[#D4AF37]" />
                    <h4 className="text-lg font-bold text-white">Business Status</h4>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">Let's verify your current situation</p>
                  
                  <PreQualQuestion 
                    question="Do you currently have an active MC authority?"
                    value={preQualData.has_active_mc}
                    onChange={(v) => setPreQualData({...preQualData, has_active_mc: v})}
                  />
                  
                  {preQualData.has_active_mc === false && (
                    <>
                      <PreQualQuestion 
                        question="Is your LLC already formed?"
                        value={preQualData.llc_formed}
                        onChange={(v) => setPreQualData({...preQualData, llc_formed: v})}
                      />
                      
                      {preQualData.llc_formed === false && (
                        <PreQualQuestion 
                          question="Are you prepared to form your LLC within 7 days?"
                          value={preQualData.ready_to_form_llc}
                          onChange={(v) => setPreQualData({...preQualData, ready_to_form_llc: v})}
                        />
                      )}
                    </>
                  )}
                  
                  {preQualData.has_active_mc === true && (
                    <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 text-sm">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-blue-300">
                          Great! Since you already have authority, Build isn't for you. 
                          Click Continue to see our Protect & Grow services instead.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {preQualData.has_active_mc === false && preQualData.llc_formed === false && preQualData.ready_to_form_llc === false && (
                    <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 text-sm">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-amber-300">
                          Build requires readiness to take immediate action. Consider returning when you're prepared to form your LLC.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Step 2 - Financial Readiness */}
              {buildPreQualStep === 2 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                    <h4 className="text-lg font-bold text-white">Financial Readiness</h4>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">Confirm your financial preparation</p>
                  
                  <PreQualQuestion 
                    question="Do you understand all government filing fees are your responsibility?"
                    value={preQualData.understands_fees}
                    onChange={(v) => setPreQualData({...preQualData, understands_fees: v})}
                  />
                  
                  <PreQualQuestion 
                    question="Do you have minimum startup funds available? (Filing fees, insurance deposits, etc.)"
                    value={preQualData.has_startup_funds}
                    onChange={(v) => setPreQualData({...preQualData, has_startup_funds: v})}
                  />
                  
                  <PreQualQuestion 
                    question="Are you prepared to secure insurance within 14 days of authority filing?"
                    value={preQualData.can_secure_insurance}
                    onChange={(v) => setPreQualData({...preQualData, can_secure_insurance: v})}
                  />
                  
                  {(preQualData.understands_fees === false || preQualData.has_startup_funds === false || preQualData.can_secure_insurance === false) && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-sm">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-red-300">
                          All questions must be "Yes" to proceed. Build requires financial readiness to move quickly.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Step 3 - Responsibility Agreement */}
              {buildPreQualStep === 3 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <FileCheck className="w-5 h-5 text-[#D4AF37]" />
                    <h4 className="text-lg font-bold text-white">Responsibility Agreement</h4>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">Confirm your understanding and commitment</p>
                  
                  <PreQualCheckbox 
                    text="I understand Queen B Logistics provides structured guidance and oversight. This is a professional service partnership, not a filing service."
                    checked={preQualData.understands_guidance}
                    onChange={(v) => setPreQualData({...preQualData, understands_guidance: v})}
                  />
                  
                  <PreQualCheckbox 
                    text="I understand Queen B Logistics does not file documents on my behalf. I am responsible for completing all government filings as directed."
                    checked={preQualData.understands_no_filing}
                    onChange={(v) => setPreQualData({...preQualData, understands_no_filing: v})}
                  />
                  
                  <PreQualCheckbox 
                    text="I agree to complete all assigned tasks within the specified deadlines. I understand delays may affect my launch timeline."
                    checked={preQualData.agrees_to_deadlines}
                    onChange={(v) => setPreQualData({...preQualData, agrees_to_deadlines: v})}
                  />
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-[#121212] border-t border-zinc-800 p-4 flex items-center justify-between">
              <button
                onClick={handlePreQualBack}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {buildPreQualStep === 1 ? 'Cancel' : 'Back'}
              </button>
              
              <button
                onClick={handlePreQualNext}
                disabled={!canProceedPreQual()}
                className={`flex items-center gap-2 px-5 py-2 font-bold text-sm transition-all ${
                  canProceedPreQual()
                    ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]'
                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
                data-testid="prequal-next-btn"
              >
                {buildPreQualStep === 3 ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirm & Select Tier
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierOnboardingPage;
